import axios from 'axios';

// Em produção usa URL relativo (mesmo servidor); em dev o proxy CRACO encaminha /api → localhost:8000
const IS_DEV = process.env.NODE_ENV === 'development';
const API = IS_DEV ? 'http://localhost:8000/api' : '/api';
const API_URL = IS_DEV ? 'http://localhost:8000' : '';

// Converte caminhos relativos de ficheiros em URLs absolutas do backend
// w = largura da miniatura (160/320/480/800); o servidor devolve webp leve e em cache
export const fileUrl = (path, w) => {
  if (!path) return null;
  if (path.startsWith('http') || path.startsWith('/static')) return path;
  const url = path.startsWith('/api') ? path : `${API}/files/${path}`;
  return w && /\.(jpe?g|png|webp)$/i.test(path) ? `${url}?w=${w}` : url;
};

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

// Força o envio de cookies em todas as chamadas
api.defaults.withCredentials = true;

// Auto-refresh access_token on 401 and retry the original request once.
let refreshPromise = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;

    if (status !== 401 || original._retried || original.url?.includes('/auth/')) {
      return Promise.reject(error);
    }

    original._retried = true;

    try {
      if (!refreshPromise) {
        refreshPromise = axios
          .post(`${API}/auth/refresh`, {}, { withCredentials: true })
          .finally(() => {
            refreshPromise = null;
          });
      }

      await refreshPromise;
      return api(original);
    } catch (refreshErr) {
      return Promise.reject(refreshErr);
    }
  }
);

// ==================== CUSTOMERS ====================
export const customersApi = {
  getAll: (search = '') => api.get(`/customers${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  getOne: (id) => api.get(`/customers/${id}`),
  getDetail: (id) => api.get(`/customers/${id}/detail`),
  getLastPrices: (id) => api.get(`/customers/${id}/last-prices`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  downloadCSV: () => downloadFile('/customers/export/csv', 'clientes.csv'),
  exportCSV: () => `${API}/customers/export/csv`
};

// ==================== SUPPLIERS ====================
export const suppliersApi = {
  getAll: (search = '') => api.get(`/suppliers${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  getOne: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`)
};

// ==================== PRODUCTS ====================
export const productsApi = {
  getAll: (params = {}) => {
    const qp = new URLSearchParams();
    if (params.search) qp.append('search', params.search);
    if (params.category) qp.append('category', params.category);
    if (params.supplier_id) qp.append('supplier_id', params.supplier_id);
    if (params.low_margin) qp.append('low_margin', 'true');
    if (params.limit) qp.append('limit', params.limit);
    if (params.slim) qp.append('slim', 'true');
    const q = qp.toString();
    return api.get(`/products${q ? `?${q}` : ''}`);
  },
  getOne: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  getCategories: () => api.get('/products/categories'),
  downloadCSV: () => downloadFile('/products/export/csv', 'produtos.csv'),
  exportCSV: () => `${API}/products/export/csv`,
  importCSV: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/products/import/csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  toggleFeatured: (id, featured) => api.put(`/products/${id}`, { featured }),
};

// ==================== DOWNLOAD HELPER ====================
async function downloadFile(path, fallbackName) {
  const res = await api.get(path, { responseType: 'blob' });
  const disp = res.headers['content-disposition'] || '';
  const match = /filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/i.exec(disp);
  const filename = match ? decodeURIComponent(match[1]) : fallbackName;
  const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

// Open PDF directly in a new tab using the API URL (relies on inline Content-Disposition + cookie auth)
function openApiUrl(path) {
  const url = `${API}${path}`;
  const win = window.open(url, '_blank');
  if (!win) {
    window.location.href = url;
  }
}

// ==================== ORDERS ====================
export const ordersApi = {
  getAll: (params = {}) => {
    const qp = new URLSearchParams();
    if (params.customer_id) qp.append('customer_id', params.customer_id);
    if (params.status) qp.append('status', params.status);
    if (params.date_from) qp.append('date_from', params.date_from);
    if (params.date_to) qp.append('date_to', params.date_to);
    if (params.page) qp.append('page', params.page);
    if (params.limit) qp.append('limit', params.limit);
    const q = qp.toString();
    return api.get(`/orders${q ? `?${q}` : ''}`);
  },
  getOne: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  update: (id, data) => api.put(`/orders/${id}`, data),
  delete: (id) => api.delete(`/orders/${id}`),
  duplicate: (id) => api.post(`/orders/${id}/duplicate`),
  downloadPDF: (id, num) => downloadFile(`/orders/${id}/pdf`, `encomenda_${num || id}.pdf`),
  getPDFBlob: (id) => api.get(`/orders/${id}/pdf`, { responseType: 'blob' }),
  openPDF: (id, num) => downloadFile(`/orders/${id}/pdf`, `encomenda_${num || id}.pdf`),
  downloadCSV: () => downloadFile('/orders/export/csv', 'encomendas.csv'),
  getPDF: (id) => `${API}/orders/${id}/pdf`,
  exportCSV: () => `${API}/orders/export/csv`,
  emailPdf: (id) => api.post(`/orders/${id}/email-pdf`),
  parseAi: (text, file) => {
    const fd = new FormData();
    if (text) fd.append('text', text);
    if (file) fd.append('file', file);
    return api.post('/orders/parse-ai', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// ==================== PURCHASES ====================
export const comprasApi = {
  getAll: (params = {}) => {
    const qp = new URLSearchParams();
    if (params.supplier_id) qp.append('supplier_id', params.supplier_id);
    if (params.status) qp.append('status', params.status);
    const q = qp.toString();
    return api.get(`/purchases${q ? `?${q}` : ''}`);
  },
  getOne: (id) => api.get(`/purchases/${id}`),
  create: (data) => api.post('/purchases', data),
  update: (id, data) => api.put(`/purchases/${id}`, data),
  delete: (id) => api.delete(`/purchases/${id}`),
  receive: (id) => api.post(`/purchases/${id}/receive`),
  parseAi: (text, file) => {
    const fd = new FormData();
    if (text) fd.append('text', text);
    if (file) fd.append('file', file);
    return api.post('/purchases/parse-ai', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// ==================== QUOTATIONS ====================
export const quotationsApi = {
  getAll: (params = {}) => {
    const qp = new URLSearchParams();
    if (params.customer_id) qp.append('customer_id', params.customer_id);
    if (params.status) qp.append('status', params.status);
    const q = qp.toString();
    return api.get(`/quotations${q ? `?${q}` : ''}`);
  },
  getOne: (id) => api.get(`/quotations/${id}`),
  create: (data) => api.post('/quotations', data),
  update: (id, data) => api.put(`/quotations/${id}`, data),
  delete: (id) => api.delete(`/quotations/${id}`),
  convert: (id) => api.post(`/quotations/${id}/convert`),
  openPDF: (id, num) => downloadFile(`/quotations/${id}/pdf`, `orcamento_${num || id}.pdf`),
  getPDF: (id) => `${API}/quotations/${id}/pdf`,
};

// ==================== PRICE LISTS ====================
export const priceListsApi = {
  getAll: () => api.get('/price-lists'),
  getOne: (id) => api.get(`/price-lists/${id}`),
  create: (data) => api.post('/price-lists', data),
  update: (id, data) => api.put(`/price-lists/${id}`, data),
  delete: (id) => api.delete(`/price-lists/${id}`)
};

// ==================== CUSTOMER PRICES ====================
export const customerPricesApi = {
  getAll: (params = {}) => {
    const qp = new URLSearchParams();
    if (params.customer_id) qp.append('customer_id', params.customer_id);
    if (params.product_id) qp.append('product_id', params.product_id);
    const q = qp.toString();
    return api.get(`/customer-prices${q ? `?${q}` : ''}`);
  },
  create: (data) => api.post('/customer-prices', data),
  delete: (id) => api.delete(`/customer-prices/${id}`)
};

// ==================== PRICE RESOLUTION ====================
export const pricesApi = {
  resolveForCustomer: (customerId) => api.get(`/prices/resolve/${customerId}`),
  resolveSingle: (customerId, productId, variantSku = null) =>
    api.get(`/prices/resolve/${customerId}/${productId}${variantSku ? `?variant_sku=${encodeURIComponent(variantSku)}` : ''}`)
};

// ==================== ACTIVITY ====================
export const activityApi = {
  getAll: (params = {}) => {
    const qp = new URLSearchParams();
    if (params.limit) qp.append('limit', params.limit);
    if (params.entity_type) qp.append('entity_type', params.entity_type);
    const q = qp.toString();
    return api.get(`/activity${q ? `?${q}` : ''}`);
  },
  clearAll: () => api.delete('/activity?confirm=true'),
};

// ==================== DASHBOARD ====================
export const dashboardApi = {
  getKPIs: (period = 'all') => api.get(`/dashboard/kpis?period=${period}`),
  getTopCustomers: (limit = 5) => api.get(`/dashboard/top-customers?limit=${limit}`),
  getTopProducts: (limit = 5) => api.get(`/dashboard/top-products?limit=${limit}`),
  getLowMarginProducts: (limit = 10) => api.get(`/dashboard/low-margin-products?limit=${limit}`),
  getMonthlySales: (months = 6) => api.get(`/dashboard/monthly-sales?months=${months}`),
  getStockAlerts: (threshold = 5) => api.get(`/dashboard/stock-alerts?threshold=${threshold}`),
  getSalesByCategory: (period = 'all') => api.get(`/dashboard/sales-by-category?period=${period}`),
};

// ==================== GLOBAL SEARCH ====================
export const searchApi = {
  search: (q) => api.get(`/search?q=${encodeURIComponent(q)}`)
};

// ==================== INVENTORY ====================
export const inventarioApi = {
  // Categories
  getCategories: () => api.get('/categories'),
  createCategory: (data) => api.post('/categories', data),
  updateCategory: (id, data) => api.put(`/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/categories/${id}`),
  // Stock
  getMovements: (params = {}) => {
    const qp = new URLSearchParams();
    if (params.product_id) qp.append('product_id', params.product_id);
    if (params.type) qp.append('type', params.type);
    if (params.limit) qp.append('limit', params.limit);
    const q = qp.toString();
    return api.get(`/stock/movements${q ? `?${q}` : ''}`);
  },
  adjustStock: (data) => api.post('/stock/adjust', data),
  transferStock: (data) => api.post('/stock/transfer', data),
  scanProduct: (code) => api.get(`/stock/scan?code=${encodeURIComponent(code)}`),
};

// ==================== CONTAGEM DE INVENTÁRIO + ETIQUETAS QR ====================
export const countsApi = {
  list: () => api.get('/inventory-counts'),
  create: (name) => api.post('/inventory-counts', { name }),
  get: (id) => api.get(`/inventory-counts/${id}`),
  setLine: (id, data) => api.post(`/inventory-counts/${id}/lines`, data),
  apply: (id) => api.post(`/inventory-counts/${id}/apply`),
  cancel: (id) => api.delete(`/inventory-counts/${id}`),
};

export const qrLabelsUrl = ({ category, productId, skip } = {}) => {
  const qp = new URLSearchParams();
  if (productId) qp.append('product_id', productId);
  else if (category) qp.append('category', category);
  if (skip) qp.append('skip', skip);
  const q = qp.toString();
  return `${API}/labels/qr${q ? `?${q}` : ''}`;
};

// Lê o texto de um QR do ERP (…/q/<produto>?v=<variante>) ou devolve o código simples
export const parseScan = (text = '') => {
  const m = text.match(/\/q\/([0-9a-f-]{36})(?:\?v=([0-9a-f-]{36}))?/i);
  return m ? { productId: m[1], variantId: m[2] || null } : { code: text.trim() };
};

// ==================== LOCATIONS ====================
export const locationsApi = {
  getAll: () => api.get('/locations'),
  create: (name) => api.post('/locations', { name }),
  delete: (id) => api.delete(`/locations/${id}`),
};

// ==================== CATALOG FLIPBOOK ====================
export const catalogApi = {
  getInfo: () => api.get('/catalog/info'),
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/catalog', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  url: () => `${API}/files/catalog/catalogo.pdf`,
};

// ==================== FILE UPLOAD ====================
export const uploadApi = {
  productPhoto: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/product-photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  productDoc: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/product-doc', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getFileUrl: (path) => `${API}/files/${path}`
};

export default api;